/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Card, Form, Radio, Row, Space, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import EntryPointSelect from './Fields/EntryPointSelect';

// Misc imports
import { ANALYTIC_TAGS_RECOMMENDATIONS } from '../../constants/flowGraph';
import { ACTIVITY_TYPES } from '../../constants/main';

const ThumbsFeedbackFormItems = ({
  responseIndex,
  form,
  canEdit
}) => {
  const { t } = useTranslation();

  const isThumbsFeedbackActive = (responseIndex) => {
    if(form.getFieldValue(['responses', responseIndex, 'inlineFeedbackData', 'enabled']) === false) {
      form.setFieldValue(['responses', responseIndex, 'enableThumbsFeedback'], false);
      return false;
    }

    if(form.getFieldValue(['responses', responseIndex, 'inlineFeedbackData', 'enabled']) === true) {
      form.setFieldValue(['responses', responseIndex, 'enableThumbsFeedback'], true);
      return true;
    }

    return undefined;
  };

  return (
    <Fragment>
      <Form.Item
        name={[responseIndex, 'enableThumbsFeedback']}
        valuePropName="checked"
        className="mb-0"
      >
        <Radio.Group
          className="w-100"
          defaultValue={form.getFieldValue(['responses', responseIndex, 'inlineFeedbackData', 'enabled'])}
          disabled={!canEdit}
          onChange={(e) => {
            const { value } = e.target;

            if (value === undefined) {
              form.setFieldValue(['responses', responseIndex, 'inlineFeedbackData'], value);
            } else {
              form.setFieldValue(['responses', responseIndex, 'inlineFeedbackData'], { enabled: value });
            }
          }}
        >
          <Space direction="vertical" className="w-100">
            <Radio value={true} className="w-100">
              <Row className="w-100">
                {t('Enable Thumb Rating')}

                <Tooltip
                  placement="right"
                  title={t('This action will overwrite any Thumb Rating configurations made in the agent\'s settings page.')}
                  className="ml-1"
                >
                  <InfoCircleOutlined />
                </Tooltip>

                {
                  isThumbsFeedbackActive(responseIndex) ? (
                    <Card
                      size="small"
                      bordered
                      className="mt-2 w-100 shadow-none"
                    >
                      <Form.Item
                        hidden
                        name={[responseIndex, 'inlineFeedbackData', 'positive', 'tags']}
                        initialValue={[ANALYTIC_TAGS_RECOMMENDATIONS.USER_FEEDBACK_POSITIVE]}
                      />

                      <Form.Item
                        label={t('Flow triggered with Thumbs Up')}
                        name={[responseIndex, 'inlineFeedbackData', 'positive', 'feedbackNodeId']}
                      >
                        <EntryPointSelect
                          allowClear={true}
                          activityTypesToDisplay={Object.values(ACTIVITY_TYPES)}
                          disabled={!canEdit}
                        />
                      </Form.Item>

                      <Form.Item
                        hidden
                        name={[responseIndex, 'inlineFeedbackData', 'negative', 'tags']}
                        initialValue={[ANALYTIC_TAGS_RECOMMENDATIONS.USER_FEEDBACK_NEGATIVE]}
                      />

                      <Form.Item
                        label={t('Flow triggered with Thumbs Down')}
                        name={[responseIndex, 'inlineFeedbackData', 'negative', 'feedbackNodeId']}
                      >
                        <EntryPointSelect
                          allowClear={true}
                          activityTypesToDisplay={Object.values(ACTIVITY_TYPES)}
                          disabled={!canEdit}
                        />
                      </Form.Item>
                    </Card>
                  ) : null
                }
              </Row>
            </Radio>

            <Radio value={false}>
              {t('Disable Thumb Rating')}

              <Tooltip
                placement="right"
                title={t('This action will overwrite any Thumb Rating configurations made in the agent\'s settings page.')}
                className="ml-1"
              >
                <InfoCircleOutlined />
              </Tooltip>
            </Radio>
            <Radio value={undefined}>{t('Default')}</Radio>
          </Space>
        </Radio.Group>
      </Form.Item>
    </Fragment>
  );
};

ThumbsFeedbackFormItems.propTypes = {
  responseIndex: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
  canEdit: PropTypes.bool.isRequired
};

export default ThumbsFeedbackFormItems;
